// @vendors
import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';

// @context
import { useAuth } from '../../context/AuthContext';

// @services
import leadPriceService from '../../services/lead-price.service';

interface LeadPriceManagerProps {
  initialPrice?: number;
  onPriceUpdated?: (newPrice: number) => void;
}

const LeadPriceManager: React.FC<LeadPriceManagerProps> = ({ initialPrice = 0, onPriceUpdated }) => {
  const [leadPrice, setLeadPrice] = useState<number>(initialPrice);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const { userInfo } = useAuth();

  useEffect(() => {
    if (initialPrice !== undefined) return setLeadPrice(initialPrice);
    if (userInfo && userInfo.leadPrice) return setLeadPrice(userInfo.leadPrice);
  }, [initialPrice, userInfo]);

  // Validar el precio del lead
  const validateLeadPrice = (price: number): boolean => {
    if (price <= 0) {
      setError('El precio del lead no puede ser cero o negativo');
      return false;
    }
    if (price > 200000) {
      setError('El precio del lead no puede ser mayor a $200,000');
      return false;
    }
    setError(null);
    return true;
  };

  // Manejar cambios en el precio del lead
  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);
    setLeadPrice(isNaN(value) ? 0 : value);
    setError(null);
    setSuccess(null);
  };

  // Manejar el envío del formulario
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateLeadPrice(leadPrice)) return;
    if (!userInfo || !userInfo.id) return setError('No se pudo identificar al usuario. Por favor, inicia sesión nuevamente.');
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await leadPriceService.updateLeadPrice(userInfo.id, leadPrice);
      setSuccess('Precio de leads actualizado correctamente');
      if (onPriceUpdated) onPriceUpdated(leadPrice);
      toast.success('Precio de leads actualizado correctamente');
      setIsEditing(false);
    } catch (err) {
      toast.error('Error al actualizar el precio de leads');
      setError('No se pudo actualizar el precio de leads. Por favor, intenta de nuevo más tarde.');
    } finally {
      setIsLoading(false);
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    }
  };

  // Formatear el precio como moneda
  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <div className="bg-xervix-bg-primary dark:bg-xervix-bg-primary-dark shadow rounded-lg p-6">
      <h2 className="text-lg font-medium text-xervix-text-primary dark:text-xervix-text-primary-dark mb-4">
        Gestión de precio por lead
      </h2>

      {!isEditing ? (
        <div className="mb-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Precio actual por lead:</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {formatCurrency(leadPrice)}
              </p>
            </div>
            <button
              onClick={() => setIsEditing(true)}
              className="px-4 py-2 bg-xervix-primary text-white rounded-md hover:bg-opacity-90 transition-colors"
              disabled={isLoading}
            >
              Editar precio
            </button>
          </div>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            Este es el valor que pagas por cada lead que te enviamos.
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="leadPrice" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Nuevo precio por lead (COP)
            </label>
            <div className="relative mt-1 rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="text-gray-500 sm:text-sm">$</span>
              </div>
              <input
                type="number"
                name="leadPrice"
                id="leadPrice"
                className={`block w-full pl-7 pr-12 py-3 rounded-md focus:ring-2 focus:outline-none ${error
                  ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                  : 'border-gray-300 focus:ring-xervix-primary focus:border-xervix-primary'
                  } dark:bg-gray-800 dark:border-gray-700 dark:text-white`}
                placeholder="0"
                value={leadPrice || ''}
                onChange={handlePriceChange}
                min="1"
                max="200000"
                disabled={isLoading}
              />
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <span className="text-gray-500 sm:text-sm">COP</span>
              </div>
            </div>
            {error && (
              <p className="mt-2 text-sm text-red-600 dark:text-red-500">{error}</p>
            )}
          </div>

          <div className="flex space-x-3">
            <button
              type="submit"
              disabled={isLoading || !leadPrice || leadPrice <= 0 || leadPrice > 200000}
              className={`px-4 py-2 rounded-md font-medium transition-colors ${isLoading || !leadPrice || leadPrice <= 0 || leadPrice > 200000
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-xervix-primary text-white hover:bg-opacity-90'
                }`}
            >
              {isLoading ? 'Actualizando...' : 'Guardar cambios'}
            </button>
            <button
              type="button"
              onClick={() => {
                setIsEditing(false);
                setLeadPrice(initialPrice);
                setError(null);
                setSuccess(null);
              }}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
              disabled={isLoading}
            >
              Cancelar
            </button>
          </div>
        </form>
      )}

      {success && (
        <div className="mt-4 p-3 bg-green-50 text-green-800 rounded-md dark:bg-green-900 dark:text-green-200">
          {success}
        </div>
      )}

      <div className="mt-4 text-sm text-gray-500 dark:text-gray-400 border-t pt-4 dark:border-gray-700">
        <p className="font-medium mb-2">¿Por qué es importante el precio por lead?</p>
        <p>El precio que estableces determina tu prioridad en la lista de proveedores para los clientes. Un precio competitivo puede aumentar tus oportunidades de negocio.</p>
      </div>
    </div>
  );
};

export default LeadPriceManager;
